import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  PlaylistAddCheck as AssignIcon,
} from "@mui/icons-material";

import { PackageMappingsApi } from "../../../../services/api/multi/package_mappings";

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const getErrorMessage = (error) => {
  if (error?.response) {
    const detail = error.response.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((e) => (typeof e === "string" ? e : e.msg || JSON.stringify(e)))
        .join("; ");
    }
    return (
      error.response.data?.message ||
      (detail ? JSON.stringify(detail) : "Server error occurred")
    );
  }
  return error?.message || "An unexpected error occurred";
};

const PACKAGE_TYPE_ORDER = ["XS", "S", "M", "L1", "L2", "L3", "L5", "XL"];

const PACKAGE_TYPE_OPTIONS = PACKAGE_TYPE_ORDER.map((t) => ({
  value: t,
  label: t,
}));

const comparePackageTypes = (left, right) => {
  const leftIndex = PACKAGE_TYPE_ORDER.indexOf(left);
  const rightIndex = PACKAGE_TYPE_ORDER.indexOf(right);
  const leftOrder = leftIndex === -1 ? Infinity : leftIndex;
  const rightOrder = rightIndex === -1 ? Infinity : rightIndex;

  return leftOrder - rightOrder;
};

const CostMappingsPanel = ({ setError, setSuccess }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    min_weight: "",
    package_type: "S",
    cost: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const loadMappings = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const CACHE_KEY = "package_mappings_cache";
      const cached = localStorage.getItem(CACHE_KEY);
      const CACHE_DURATION = 5 * 60 * 1000;

      if (!forceRefresh && cached && typeof forceRefresh !== "object") {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setMappings(data);
          setLoading(false);
          return;
        }
      }

      const response = await PackageMappingsApi.getPackageMappings();
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: response.data || [],
          timestamp: Date.now(),
        }),
      );
      setMappings(response.data || []);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMappings();
  }, []);

  const resetForm = () => {
    setFormData({
      min_weight: "",
      package_type: "S",
      cost: "",
    });
    setEditingId(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    min_weight: formData.min_weight ? parseFloat(formData.min_weight) : null,
    package_type: formData.package_type,
    cost: parseFloat(formData.cost),
  });

  const handleCreate = async () => {
    try {
      setLoading(true);
      setError(null);
      await PackageMappingsApi.createPackageMapping(buildPayload());
      setSuccess("Package mapping created successfully!");
      setCreateDialogOpen(false);
      resetForm();
      await loadMappings(true);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      setError(null);
      await PackageMappingsApi.updatePackageMapping(editingId, buildPayload());
      setSuccess("Package mapping updated successfully!");
      setEditDialogOpen(false);
      resetForm();
      await loadMappings(true);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);
      await PackageMappingsApi.deletePackageMapping(deletingId);
      setSuccess("Package mapping deleted successfully!");
      setDeleteDialogOpen(false);
      setDeletingId(null);
      await loadMappings(true);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAll = async () => {
    try {
      setAssigning(true);
      setError(null);
      const response = await PackageMappingsApi.assignPackages();
      setSuccess(
        `Assigned packages for ${response.data.updated ?? 0} article(s)` +
          (response.data.changed
            ? ` — ${response.data.changed} package change(s)` +
              (response.data.recalc_started ? "; forced recalc started" : "")
            : "") +
          (response.data.csv_assigned != null
            ? ` — CSV: ${response.data.csv_assigned}, fallback: ${response.data.fallback_assigned}`
            : ""),
      );
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setAssigning(false);
    }
  };

  const mappingFormFields = (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required>
          <InputLabel>Package Type</InputLabel>
          <Select
            value={formData.package_type}
            label="Package Type"
            onChange={(e) => handleInputChange("package_type", e.target.value)}
          >
            {PACKAGE_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Cost (€)"
          type="number"
          inputProps={{ step: "0.01" }}
          value={formData.cost}
          onChange={(e) => handleInputChange("cost", e.target.value)}
          required
          helperText="Required — each package type has its own cost"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Min Weight (g)"
          type="number"
          value={formData.min_weight}
          onChange={(e) => handleInputChange("min_weight", e.target.value)}
          helperText="Optional — used only when the CSV has no GTIN match; the highest min weight not exceeding the article weight wins."
        />
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Configure costs for package types{" "}
        <strong>XS, S, M, L1, L2, L3, L5, XL</strong>. Optional minimum-weight
        thresholds are used only when a GTIN is missing from the CSV.
      </Typography>

      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setCreateDialogOpen(true);
          }}
        >
          Add Package Mapping
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={
            assigning ? <CircularProgress size={16} /> : <AssignIcon />
          }
          onClick={handleAssignAll}
          disabled={assigning || loading}
        >
          Assign packages (all SKUs)
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => loadMappings(true)}
        >
          Refresh
        </Button>
      </Box>

      <Card>
        <CardContent>
          {loading && mappings.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Package Type</TableCell>
                    <TableCell>Cost (€)</TableCell>
                    <TableCell>Min Weight (g)</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...mappings]
                    .sort((left, right) =>
                      comparePackageTypes(
                        left.package_type,
                        right.package_type,
                      ),
                    )
                    .map((mapping) => (
                      <TableRow key={mapping.id}>
                        <TableCell>
                          <Chip label={mapping.package_type} size="small" />
                        </TableCell>
                        <TableCell>€{mapping.cost}</TableCell>
                        <TableCell>{mapping.min_weight ?? "—"}</TableCell>
                        <TableCell>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => {
                                setFormData({
                                  min_weight:
                                    mapping.min_weight?.toString() || "",
                                  package_type: mapping.package_type || "S",
                                  cost: mapping.cost?.toString() || "",
                                });
                                setEditingId(mapping.id);
                                setEditDialogOpen(true);
                              }}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => {
                                setDeletingId(mapping.id);
                                setDeleteDialogOpen(true);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  {mappings.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No package mappings yet. Add one cost row per package
                        type.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Package Mapping</DialogTitle>
        <DialogContent>{mappingFormFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={loading || !formData.cost || !formData.package_type}
          >
            {loading ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Package Mapping</DialogTitle>
        <DialogContent>{mappingFormFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={loading || !formData.cost || !formData.package_type}
          >
            {loading ? <CircularProgress size={20} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Delete this package mapping? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const CsvUploadPanel = ({ setError, setSuccess }) => {
  const [status, setStatus] = useState({
    exists: false,
    loading: false,
    row_count: null,
    last_modified: null,
    size: null,
  });
  const [uploading, setUploading] = useState(false);

  const refreshStatus = async () => {
    try {
      setStatus((prev) => ({ ...prev, loading: true }));
      const response = await PackageMappingsApi.checkCsvExists();
      setStatus({
        exists: response.data.exists,
        loading: false,
        row_count: response.data.row_count,
        last_modified: response.data.last_modified,
        size: response.data.size,
        filename: response.data.filename,
      });
    } catch (error) {
      setStatus((prev) => ({ ...prev, loading: false }));
      setError(getErrorMessage(error));
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      setError(null);
      const response = await PackageMappingsApi.uploadCsv(file);
      setSuccess(
        `CSV uploaded (${response.data.row_count} GTINs). File is also available in the File Manager under multi_repricer_uploads/package_mappings.csv.`,
      );
      await refreshStatus();
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleDownload = async () => {
    try {
      const response = await PackageMappingsApi.downloadCsv();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "package_mappings.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Upload a tab- or comma-separated CSV with columns{" "}
        <strong>Artikelname, GTIN, Name, Wert</strong>. Stored as{" "}
        <code>uploaded_files/package_mappings.csv</code> (visible in File
        Manager).
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Wert mapping: BOX-XS-* → XS, BOX-S-* → S, BOX-M-* → M, BOX-L-1/2/3/5 →
        L1/L2/L3/L5, BOX-XL-* → XL, NO-BOX → no package.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        {status.loading ? (
          <CircularProgress size={24} />
        ) : status.exists ? (
          <Box>
            <Chip
              icon={<CheckCircleIcon />}
              label="CSV present"
              color="success"
              size="small"
              sx={{ mb: 1 }}
            />
            <Typography variant="body2">
              Rows (GTINs): {status.row_count ?? "—"}
            </Typography>
            <Typography variant="body2">
              Size:{" "}
              {status.size != null
                ? `${(status.size / 1024).toFixed(1)} KB`
                : "—"}
            </Typography>
            <Typography variant="body2">
              Last modified: {status.last_modified || "—"}
            </Typography>
          </Box>
        ) : (
          <Alert severity="info">No package mappings CSV uploaded yet.</Alert>
        )}
      </Paper>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadFileIcon />}
          disabled={uploading}
        >
          {uploading ? <CircularProgress size={20} /> : "Upload CSV"}
          <input
            type="file"
            hidden
            accept=".csv,.tsv,.txt"
            onChange={handleUpload}
          />
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!status.exists}
        >
          Download
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refreshStatus}
        >
          Refresh status
        </Button>
      </Box>
    </Box>
  );
};

const SftpPanel = ({ setError, setSuccess }) => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({
    label: "",
    host: "",
    port: 22,
    username: "",
    password: "",
    path: "",
    active: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const loadCredentials = async () => {
    try {
      setLoading(true);
      const response = await PackageMappingsApi.listSftpCredentials();
      setCredentials(response.data || []);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCredentials();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      const payload = {
        label: form.label || null,
        host: form.host,
        port: parseInt(form.port, 10) || 22,
        username: form.username,
        path: form.path,
        active: form.active,
      };
      if (form.password) {
        payload.password = form.password;
      } else if (!editingId) {
        setError("Password is required for new SFTP credentials");
        setLoading(false);
        return;
      }

      if (editingId) {
        await PackageMappingsApi.updateSftpCredential(editingId, payload);
        setSuccess("SFTP credential updated");
      } else {
        if (!payload.password) {
          setError("Password is required");
          setLoading(false);
          return;
        }
        await PackageMappingsApi.createSftpCredential(payload);
        setSuccess("SFTP credential saved (password encrypted at rest)");
      }
      setEditingId(null);
      setForm({
        label: "",
        host: "",
        port: 22,
        username: "",
        password: "",
        path: "",
        active: true,
      });
      await loadCredentials();
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setError(null);
      let response;
      if (editingId && !form.password) {
        response = await PackageMappingsApi.testSftpCredential(editingId);
      } else {
        response = await PackageMappingsApi.testSftp({
          host: form.host,
          port: parseInt(form.port, 10) || 22,
          username: form.username,
          password: form.password || "",
          path: form.path || null,
        });
      }
      setTestResult(response.data);
      if (response.data.success && response.data.csv_ok) {
        setSuccess(response.data.message);
      } else if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setTesting(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await PackageMappingsApi.refreshCsvFromSftp();
      setSuccess(`CSV refreshed from SFTP (${response.data.row_count} GTINs)`);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCredential = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);
      const response =
        await PackageMappingsApi.downloadCsvFromSftpCredential(credentialId);
      const name = response.data.csv_filename || "CSV";
      setSuccess(
        `Downloaded ${name} from SFTP (${response.data.row_count} GTINs)`,
      );
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Configure SFTP to auto-refresh the package mappings CSV on each base
        repricer run. Password is stored encrypted (Fernet). Path must be the
        remote <strong>directory</strong> that holds the export files (e.g.{" "}
        <code>/srv/data/jtl-package-export</code>). If multiple CSVs exist, the
        one whose filename contains <strong>JTL</strong> is downloaded.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Label"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Host"
            value={form.host}
            onChange={(e) => setForm({ ...form, host: e.target.value })}
            required
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Port"
            type="number"
            value={form.port}
            onChange={(e) => setForm({ ...form, port: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="User"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            helperText={
              editingId
                ? "Leave blank to keep the existing password"
                : "Required"
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Remote directory"
            value={form.path}
            onChange={(e) => setForm({ ...form, path: e.target.value })}
            placeholder="/srv/data/jtl-package-export"
            helperText="Folder on the SFTP server; prefers the .csv with JTL in the name"
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
            }
            label="Active (used on base repricer run)"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !form.host || !form.username || !form.path}
        >
          {editingId ? "Update" : "Save"} credential
        </Button>
        <Button
          variant="outlined"
          onClick={handleTest}
          disabled={testing || !form.host || !form.username}
        >
          {testing ? <CircularProgress size={20} /> : "Test connection"}
        </Button>
        <Button variant="outlined" onClick={handleRefresh} disabled={loading}>
          Download CSV now
        </Button>
        {editingId && (
          <Button
            onClick={() => {
              setEditingId(null);
              setForm({
                label: "",
                host: "",
                port: 22,
                username: "",
                password: "",
                path: "",
                active: true,
              });
            }}
          >
            Cancel edit
          </Button>
        )}
      </Box>

      {testResult && (
        <Alert
          severity={
            testResult.success && testResult.csv_ok
              ? "success"
              : testResult.success
                ? "warning"
                : "error"
          }
          sx={{ mb: 2 }}
        >
          {testResult.message}
          {testResult.path_exists === false && " (directory not found)"}
          {testResult.csv_filename && ` → ${testResult.csv_filename}`}
          {testResult.csv_count != null &&
            testResult.csv_count !== 1 &&
            ` (${testResult.csv_count} CSV files)`}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Host</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Path</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {credentials.map((cred) => (
              <TableRow key={cred.id}>
                <TableCell>{cred.label || "—"}</TableCell>
                <TableCell>
                  {cred.host}:{cred.port}
                </TableCell>
                <TableCell>{cred.username}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {cred.path}
                </TableCell>
                <TableCell>
                  {cred.active ? (
                    <Chip label="Active" color="success" size="small" />
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Download CSV from this path">
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={loading}
                      onClick={() => handleDownloadCredential(cred.id)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingId(cred.id);
                      setForm({
                        label: cred.label || "",
                        host: cred.host || "",
                        port: cred.port || 22,
                        username: cred.username || "",
                        password: "",
                        path: cred.path || "",
                        active: !!cred.active,
                      });
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={async () => {
                      try {
                        await PackageMappingsApi.deleteSftpCredential(cred.id);
                        setSuccess("SFTP credential deleted");
                        await loadCredentials();
                      } catch (error) {
                        setError(getErrorMessage(error));
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {credentials.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No SFTP credentials saved yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const PackageMappings = () => {
  const [subTab, setSubTab] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Package Mappings
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
        Cost mappings, GTIN CSV upload, and SFTP auto-refresh for base repricer
        package assignment.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
        <Tabs
          value={subTab}
          onChange={(_, v) => setSubTab(v)}
          aria-label="package mappings subtabs"
        >
          <Tab label="Cost mappings" />
          <Tab label="CSV upload" />
          <Tab label="SFTP connection" />
        </Tabs>
      </Box>

      <TabPanel value={subTab} index={0}>
        <CostMappingsPanel setError={setError} setSuccess={setSuccess} />
      </TabPanel>
      <TabPanel value={subTab} index={1}>
        <CsvUploadPanel setError={setError} setSuccess={setSuccess} />
      </TabPanel>
      <TabPanel value={subTab} index={2}>
        <SftpPanel setError={setError} setSuccess={setSuccess} />
      </TabPanel>
    </Box>
  );
};

export default PackageMappings;
